import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { CircleAlert, XIcon } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  dashboardMutedGrayTextClassName,
  dashboardSidebarSkeletonClassName,
} from "./dashboardChartCardShell";
import { dashboardMiniStatsGridTemplateColumns } from "../../../../utils/miniStatGridLayout";
import PnLOverTimeChart from "../../Statistics/components/PnLOverTimeChart";
import TradesTable from "../../TradeLog/components/TradesTable";
import TradeDetailsInlinePanel, {
  type TradeDetailsInlinePanelHandle,
} from "../../TradeLog/components/TradeDetailsInlinePanel";
import TradeLogMiniStatistics from "../../TradeLog/components/TradeLogMiniStatistics";
import { useActiveBrokerAccount } from "../../../../hooks/useActiveBrokerAccount";
import type { Trade, TradeStatisticsFilters } from "../../../../types/trade";
import { computeTradingDayStats } from "../../../../utils/positionSemantics";

function formatCurrencyCompact(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    notation: Math.abs(value) >= 1000 ? "compact" : "standard",
  }).format(value);
}

export interface TradingActivityDayModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDayLabel: string;
  isDayTradesLoading: boolean;
  dayTradesError: string | null;
  dayTrades: Trade[];
  brokerAccountCode?: string;
  effectiveTimeZone: string;
  /** Resets table sort/pagination when the calendar day changes */
  dayCalendarKey?: string | null;
}

const TradingActivityDayModal: React.FC<TradingActivityDayModalProps> = ({
  isOpen,
  onClose,
  selectedDayLabel,
  isDayTradesLoading,
  dayTradesError,
  dayTrades,
  brokerAccountCode,
  effectiveTimeZone,
  dayCalendarKey,
}) => {
  const { activeBrokerName } = useActiveBrokerAccount();
  const isMobile = useIsMobile();
  const [tableTrades, setTableTrades] = useState<Trade[]>(dayTrades);
  const [detailsTradeId, setDetailsTradeId] = useState<string | null>(null);
  const inlinePanelRef = useRef<TradeDetailsInlinePanelHandle | null>(null);

  useEffect(() => {
    setTableTrades(dayTrades);
    setDetailsTradeId(null);
  }, [dayTrades, dayCalendarKey]);

  useEffect(() => {
    if (!isOpen) {
      setDetailsTradeId(null);
    }
  }, [isOpen]);

  const dayStats = useMemo(() => computeTradingDayStats(tableTrades), [tableTrades]);

  const dayStatCards = useMemo(
    () => [
      { title: "Trades", amount: String(dayStats.totalTrades) },
      { title: "Day P&L", amount: formatCurrencyCompact(dayStats.dayPnl) },
      { title: "Win rate", amount: `${dayStats.winRate.toFixed(2)}%` },
      { title: "Expectancy", amount: formatCurrencyCompact(dayStats.expectancy) },
    ],
    [dayStats],
  );

  const statsGridTemplateColumns = useMemo(() => {
    if (!isMobile) {
      return "minmax(0, 1fr)";
    }
    return dashboardMiniStatsGridTemplateColumns(dayStatCards, {
      isMobile: true,
      gapPx: 12,
    });
  }, [dayStatCards, isMobile]);

  const chartFilters = useMemo((): TradeStatisticsFilters | undefined => {
    if (!dayCalendarKey || !brokerAccountCode) return undefined;
    return {
      broker_account_code: brokerAccountCode,
      local_start_date: dayCalendarKey,
      local_end_date: dayCalendarKey,
      timezone: effectiveTimeZone,
      ...(activeBrokerName ? { broker_name: activeBrokerName } : {}),
    };
  }, [activeBrokerName, brokerAccountCode, dayCalendarKey, effectiveTimeZone]);

  const dismissExpandedTrade = useCallback((nextId: string | null = null) => {
    setDetailsTradeId(nextId);
  }, []);

  const navigateExpandedTrade = useCallback(
    (trade: Trade) => {
      const closing = detailsTradeId === trade.id;
      if (closing) {
        dismissExpandedTrade(null);
        return;
      }
      setDetailsTradeId(trade.id);
    },
    [detailsTradeId, dismissExpandedTrade],
  );

  const requestExpandedTradeNavigation = useCallback(
    (action: () => void) => {
      if (detailsTradeId != null && inlinePanelRef.current?.isDirty()) {
        inlinePanelRef.current.requestNavigate(action);
        return;
      }
      action();
    },
    [detailsTradeId],
  );

  const handleJournalSaved = useCallback((updatedTrade: Trade) => {
    setTableTrades((prev) =>
      prev.map((row) => (row.id === updatedTrade.id ? updatedTrade : row)),
    );
  }, []);

  const handleRowClick = useCallback(
    (trade: Trade) => {
      requestExpandedTradeNavigation(() => navigateExpandedTrade(trade));
    },
    [navigateExpandedTrade, requestExpandedTradeNavigation],
  );

  const handleExpandedRowDismiss = useCallback(() => {
    requestExpandedTradeNavigation(() => dismissExpandedTrade(null));
  }, [dismissExpandedTrade, requestExpandedTradeNavigation]);

  useEffect(() => {
    if (!detailsTradeId) return;
    if (!tableTrades.some((row) => row.id === detailsTradeId)) {
      dismissExpandedTrade(null);
    }
  }, [tableTrades, detailsTradeId, dismissExpandedTrade]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-trading-activity-day-modal=""
        showCloseButton={false}
        className={cn(
          "flex !h-[86vh] !max-h-[86vh] !w-[88vw] !max-w-[88vw] flex-col gap-0 overflow-hidden p-0 sm:!max-w-[88vw]",
        )}
      >
        <DialogClose asChild>
          <button
            type="button"
            data-trading-activity-day-dismiss=""
            aria-label="Close"
            className="absolute top-6 right-6 z-10"
          >
            <XIcon className="size-4" />
          </button>
        </DialogClose>

        <DialogHeader className="shrink-0 space-y-1 border-b px-6 py-4 pr-14">
          <DialogTitle>{selectedDayLabel || "Trading activity"}</DialogTitle>
          <DialogDescription
            className={cn(
              "font-[family-name:var(--font-primary)] text-sm font-normal",
              dashboardMutedGrayTextClassName,
            )}
          >
            Closed trades tied to this reporting calendar day for your active account.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          {dayTradesError ? (
            <Alert variant="destructive" className="mb-4 rounded-xl">
              <CircleAlert />
              <AlertDescription>{dayTradesError}</AlertDescription>
            </Alert>
          ) : null}

          <Grid
            templateColumns={{ base: "1fr", md: "repeat(4, minmax(0, 1fr))" }}
            gap={6}
            mb={4}
            alignItems="stretch"
            w="100%"
            minH={{ md: "min(360px, 42vh)" }}
            data-trade-log-mini-stats
          >
            <GridItem
              colSpan={{ base: 1, md: 1 }}
              display="flex"
              flexDirection="column"
              minH={{ base: "auto", md: "100%" }}
            >
              {isDayTradesLoading ? (
                <Box
                  display="grid"
                  gap={3}
                  w="100%"
                  minW={0}
                  style={{ gridTemplateColumns: statsGridTemplateColumns }}
                >
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      className={cn("min-h-[100px] rounded-lg", dashboardSidebarSkeletonClassName)}
                    />
                  ))}
                </Box>
              ) : (
                <Box
                  display="grid"
                  gap={3}
                  w="100%"
                  minW={0}
                  data-mini-stats-grid=""
                  data-trading-activity-day-mini-stats=""
                  style={{ gridTemplateColumns: statsGridTemplateColumns }}
                >
                  <Box w="100%" minW={0}>
                    <TradeLogMiniStatistics
                      title="Trades"
                      amount={String(dayStats.totalTrades)}
                      percentage={null}
                      showPercentage={false}
                      backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                      tooltip={`All trade rows for this day (${dayStats.closedTradeCount} closed${
                        dayStats.openTradeCount > 0
                          ? `, ${dayStats.openTradeCount} open`
                          : ""
                      }). Includes partial closes and open remainders from broker sync.`}
                      fitAmount
                    />
                  </Box>
                  <Box w="100%" minW={0}>
                    <TradeLogMiniStatistics
                      title="Day P&L"
                      amount={formatCurrencyCompact(dayStats.dayPnl)}
                      percentage={null}
                      showPercentage={false}
                      tooltip="Sum of realized net P&L for closed rows with a numeric P&L on this day (partial rows without P&L yet are excluded)."
                      fitAmount
                    />
                  </Box>
                  <Box w="100%" minW={0}>
                    <TradeLogMiniStatistics
                      title="Win rate"
                      amount={`${dayStats.winRate.toFixed(2)}%`}
                      percentage={null}
                      showPercentage={false}
                      backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                      tooltip="Share of closed trades on this day that closed with a profit (breakevens excluded)."
                      fitAmount
                    />
                  </Box>
                  <Box w="100%" minW={0}>
                    <TradeLogMiniStatistics
                      title="Expectancy"
                      amount={formatCurrencyCompact(dayStats.expectancy)}
                      percentage={null}
                      showPercentage={false}
                      backgroundGradient={
                        dayStats.expectancySample === 0
                          ? "linear-gradient(135deg, #38BBD1, #3EEAEC)"
                          : dayStats.expectancy >= 0
                            ? "linear-gradient(135deg, #06E363, #AAFAB1)"
                            : "linear-gradient(135deg, #E00101, #FE4244)"
                      }
                      tooltip="Average realized net P&L per closed trade on this day (same closed rows as Day P&L)."
                      fitAmount
                    />
                  </Box>
                </Box>
              )}
            </GridItem>
            <GridItem
              colSpan={{ base: 1, md: 3 }}
              display="flex"
              flexDirection="column"
              minH={{ base: "260px", md: "100%" }}
            >
              {chartFilters ? (
                <PnLOverTimeChart
                  filters={chartFilters}
                  breakdown="hourly"
                  surfaceChrome="dashboard-sidebar"
                />
              ) : null}
            </GridItem>
          </Grid>

          {isDayTradesLoading ? (
            <Skeleton
              className={cn("h-[320px] w-full rounded-xl", dashboardSidebarSkeletonClassName)}
            />
          ) : (
            <Box
              data-trading-activity-day-modal-trades-table=""
              className="min-w-0 w-full max-w-full"
            >
              <TradesTable
                key={dayCalendarKey ?? "trading-activity-day"}
                trades={tableTrades}
                hideOuterCard
                hideTableBorder={false}
                showColumnVisibility={false}
                initialSortField="exitDate"
                initialSortDirection="desc"
                hideSelectionColumn
                enableHorizontalScrollOnMobile
                paginationBelowTable
                expandedRowId={detailsTradeId}
                onExpandedRowDismiss={handleExpandedRowDismiss}
                onRowClick={handleRowClick}
                renderExpandedRow={(trade) => (
                  <TradeDetailsInlinePanel
                    ref={inlinePanelRef}
                    trade={trade}
                    onJournalSaved={handleJournalSaved}
                  />
                )}
              />
            </Box>
          )}
        </div>

        <DialogFooter className="shrink-0 sm:justify-end">
          <DialogClose asChild>
            <button type="button" data-trading-activity-day-close="">
              Close
            </button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TradingActivityDayModal;
